import React, { createContext, useState, useCallback } from "react";
import { getNonPrintedOrEmailedWorkOrders, getWorkOrder } from "../services/workOrderService";
import { parseDateToUSFormat } from "../helpers/DateHelper";
import { validateNullOrEmpty } from "../services/helpers/workOrderHelper";
import useLoading from "../hooks/useLoading";
import useRetryPolicy from "../hooks/useRetryPolicy";

const { REACT_APP_FUTURE_DAYS_WO, REACT_APP_PAST_DAYS_WO } = process.env;
const FILTERS_BY_TAB = {
  0: {},
  1: {
    futureDays: REACT_APP_FUTURE_DAYS_WO,
    pastDays: REACT_APP_PAST_DAYS_WO,
    excludeInputTimeExpected: true,
  },
};
const { DateTime } = require("luxon");
const WorkOrdersContext = createContext({
  futureWOs: [],
  todayWorkOrders: [],
});

export const WorkOrdersProvider = ({ children }) => {
  const [todayWorkOrders, setTodayWorkOrders] = useState([]);
  const [futureWOs, setFutureWOs] = useState([]);
  const [selectedWO, setSelectedWO] = useState({});
  const [isWOSelected, setIsWOSelected] = useState(false);
  const [removedWO, setRemovedWO] = useState([])
  const { setIsLoading, setIsFirstWOLoad } = useLoading();
  const { executeWithRetry } = useRetryPolicy();

  const fetchWorkOrders = async (storeId, displayLoading) => {
    try {
      setIsLoading(displayLoading);
      console.log("initializing WO load")
      await loadWorkOrdersFromStore(storeId);
      console.log("finalazing WO load")
    } catch (error) {
      console.log("Error: ", error);
      throw error
    } finally {
      setIsLoading(false);
      setIsFirstWOLoad(false)
    }
  };

  const cleanWorkOrders = () => {
    setFutureWOs([]);
    setTodayWorkOrders([]);
    setSelectedWO({});
    setIsWOSelected(false);
    setRemovedWO([]);
  }

  const loadWorkOrdersFromStore = async (storeId) => {
    const today = DateTime.now().setZone("America/Los_Angeles").toISO();
    for (let i = 0; i < 2;) {
      let lastScannedWO = ""
      let orders = []
      do {
        let res = await getNonPrintedOrEmailedWorkOrders(storeId, today, FILTERS_BY_TAB[i], lastScannedWO)
        orders = orders.concat(res.workOrders.map((wo) => ({
          ...wo,
          Phone: validateNullOrEmpty([wo.Phone]),
          SearchablePhone: wo.Phone ? wo.Phone.replace(/\D/g, '') : '',
          YearMakeModel: validateNullOrEmpty([wo.Year, wo.Make, wo.Model]),
          Year: wo.Year ?? "",
          Make: wo.Make ?? "",
          Model: wo.Model ?? "",
          License: wo.License ?? "",
          wasPrintedOrEmailed: !!wo.ServiceDate,
          Appointment: parseDateToUSFormat(wo.TimeExpected),
        })));
        lastScannedWO = res.lastScannedWO;
      } while (lastScannedWO !== "")
      i == 0 ? setTodayWorkOrders(...todayWorkOrders, orders) : setFutureWOs(...futureWOs, orders);
      i++;
    }
  };

  const fetchNewOrders = async (storeId, workOrders) => {
    const todayWO = []
    const otherWO = []
    for (let i = 0; i < workOrders.length; i++) {
      try {
        const wo = workOrders[i]
        if (wo.evtName === "REMOVE") {
          setFutureWOs((prevorders) => { return prevorders.filter(futureWo => futureWo.WorkOrder != wo.workOrderId) })
          setTodayWorkOrders((prevorders) => { return prevorders.filter(todayWo => todayWo.WorkOrder != wo.workOrderId) })
        }
        else {
          let { workOrder, isForToday, isInvalid } = await getWorkOrder(
            storeId,
            wo.workOrderId
          );
          if (isInvalid) continue

          const order = {
            ...workOrder,
            Phone: validateNullOrEmpty([workOrder.Phone]),
            YearMakeModel: validateNullOrEmpty([workOrder.Year, workOrder.Make, workOrder.Model]),
            Year: workOrder.Year ? workOrder.Year : "",
            Make: workOrder.Make ? workOrder.Make : "",
            Model: workOrder.Model ? workOrder.Model : "",
            License: workOrder.License ? workOrder.License : "",
            wasPrintedOrEmailed: !!workOrder.ServiceDate,
            Appointment: parseDateToUSFormat(workOrder.TimeExpected),
          };

          isForToday ? todayWO.push(order) : otherWO.push(order)
          updateNewOrders(todayWO, otherWO)
        }
      }
      catch (error) {
        console.log("Error: ", error);
        throw error;
      }
    }

  };

  const updateNewOrders = (todayWO, otherWO) => {
    setTodayWorkOrders((prevorders) => {
      return validateNewWOs(prevorders, todayWO);
    })
    setFutureWOs((prevorders) => {
      return validateNewWOs(prevorders, otherWO);
    });
  }

  const validateNewWOs = (originalWOs, newWOs) => {
    const originalWOsFiltered = originalWOs.filter(w1 => newWOs.some(w2 => w1.WorkOrder !== w2.WorkOrder) || newWOs.length === 0)
    const newWOsFiltered = newWOs.filter(nw => !nw.wasPrintedOrEmailed);
    const finalWOs = [...originalWOsFiltered, ...newWOsFiltered]
    return finalWOs
  }

  const contextValue = {
    futureWOs,
    todayWorkOrders,
    fetchWorkOrders: useCallback(
      async (storeId, displayLoading) => await executeWithRetry(fetchWorkOrders, storeId, displayLoading),
      []
    ),
    fetchNewOrders: useCallback(
      async (storeId, workOrders) => await executeWithRetry(fetchNewOrders, storeId, workOrders),
      []
    ),
    cleanWorkOrders,
    isWOSelected,
    selectedWO,
    setIsWOSelected,
    setSelectedWO,
    setRemovedWO,
    removedWO,
  };

  return (
    <WorkOrdersContext.Provider value={contextValue}>
      {children}
    </WorkOrdersContext.Provider>
  );
};

export default WorkOrdersContext;
